import React, { useState, useEffect, useMemo, useCallback } from "react";
import Utils from "../util/Utils";
import { format, parseISO } from "date-fns";
import Constants, { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";
// Utils import is removed as API calls are now in PortfolioContainer/Home.js

import {
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tooltip as MuiTooltip,
  CircularProgress, // Ensured import for CircularProgress
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { Search } from "semantic-ui-react";
import _ from "lodash";
import SymbolSearch from "../components/SymbolSearch";
import { usePortfolioCalculations } from "../hooks/usePortfolioCalculations";

// --- Helper Functions ---
const formatDollarValue = (value, showSign = false) => {
  const number = Number(value);
  if (isNaN(number)) return "$0.00";
  const options = { style: "currency", currency: "USD" };
  let formatted = new Intl.NumberFormat("en-US", options).format(
    Math.abs(number)
  );

  if (number < 0) {
    formatted = `-${formatted.replace("$", "")}`;
  } else if (showSign && number > 0) {
    formatted = `+${formatted}`;
  }
  return formatted;
};

const formatPercentageValue = (value, showSign = false) => {
  const number = Number(value);
  if (isNaN(number) || !isFinite(number)) return "0.00%";

  let formatted = `${Math.abs(number).toFixed(2)}%`;

  if (number < 0) {
    formatted = `-${formatted}`;
  } else if (showSign && number > 0) {
    formatted = `+${formatted}`;
  }
  return formatted;
};

// --- StockRow Component ---
const StockRow = ({
  stock,
  onSave,
  onEdit,
  onDelete,
  isEditing,
  onCancelEdit,
  onInputChange,
  totalCurrentMarketValue,
  isMobile,
  showTodaysGainColumns = false,
}) => {
  const [editedStock, setEditedStock] = useState(stock);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [priceFetchError, setPriceFetchError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setEditedStock(stock);
  }, [stock]);

  const handleLocalInputChange = (e) => {
    const { name, value } = e.target;
    let newEditedStock = { ...editedStock, [name]: value };

    if (name === "symbol") {
      newEditedStock = { ...newEditedStock, [name]: value.toUpperCase() };
      if (value.trim() === "") {
        newEditedStock = { ...newEditedStock, currentPrice: "", symbol: "" };
        setPriceFetchError(null);
      }
    }

    if ((name === "quantity" || name === "costPerStock") && value === "") {
      newEditedStock = { ...newEditedStock, [name]: "" };
    }

    setEditedStock(newEditedStock);

    if (onInputChange) {
      onInputChange(newEditedStock);
    }

    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFetchPrice = useCallback(
    async (symbolToFetch) => {
      if (
        !symbolToFetch ||
        !API_KEYS.FMP_API_KEY ||
        API_KEYS.FMP_API_KEY === "YOUR_FINANCIAL_MODELING_PREP_API_KEY"
      ) {
        setPriceFetchError("API key not configured or symbol is empty.");
        if (API_KEYS.FMP_API_KEY === "YOUR_FINANCIAL_MODELING_PREP_API_KEY") {
          console.warn(
            "Please configure your Financial Modeling Prep API key."
          );
        }
        setEditedStock((prev) => {
          const updated = { ...prev, currentPrice: "" };
          if (onInputChange) onInputChange(updated);
          return updated;
        });
        return;
      }
      setIsFetchingPrice(true);
      setPriceFetchError(null);
      try {
        const response = await fetch(
          `${FMP_API_ENDPOINTS.QUOTE(symbolToFetch.toUpperCase())}?${
            API_KEYS.FMP_API_KEY
          }`
        );
        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ ErrorMessage: "Unknown API error" }));
          throw new Error(
            errorData.ErrorMessage ||
              `FMP API request failed: ${response.status}`
          );
        }
        const data = await response.json();
        if (data && data.length > 0 && data[0].price != null) {
          const fetchedPrice = data[0].price;
          setEditedStock((prev) => {
            const updatedWithPrice = { ...prev, currentPrice: fetchedPrice };
            if (onInputChange) {
              onInputChange(updatedWithPrice);
            }
            return updatedWithPrice;
          });
        } else {
          setEditedStock((prev) => {
            const updated = { ...prev, currentPrice: "" };
            if (onInputChange) onInputChange(updated);
            return updated;
          });
          throw new Error(
            "Price not found for symbol or invalid response structure."
          );
        }
      } catch (err) {
        console.error("Failed to fetch price:", err);
        setPriceFetchError(err.message);
        setEditedStock((prev) => {
          const updated = { ...prev, currentPrice: "" };
          if (onInputChange) onInputChange(updated);
          return updated;
        });
      } finally {
        setIsFetchingPrice(false);
      }
    },
    [onInputChange]
  );

  const handleSymbolBlur = (e) => {
    const newSymbol = e.target.value.trim();
    if (newSymbol) {
      // Always fetch price when there's a symbol, regardless of whether it changed
      handleFetchPrice(newSymbol);
    } else if (editedStock.symbol !== "") {
      setEditedStock((prev) => {
        const clearedState = { ...prev, symbol: "", currentPrice: "" };
        if (onInputChange) {
          onInputChange(clearedState);
        }
        return clearedState;
      });
      setPriceFetchError(null);
    }
  };

  const handleEditClick = () => {
    onEdit(stock.id);
    if (
      editedStock.symbol &&
      (editedStock.currentPrice === "" ||
        editedStock.currentPrice == null ||
        Number(editedStock.currentPrice) === 0)
    ) {
      handleFetchPrice(editedStock.symbol);
    }
  };

  const handleSave = () => onSave(editedStock);
  const handleCancel = () => {
    onCancelEdit();
    setEditedStock(stock);
    setPriceFetchError(null);
  };
  const handleDeleteClick = () => {
    if (stock.id) {
      setDeleteConfirmOpen(true);
    }
  };
  const handleDeleteConfirm = async () => {
    if (stock.id) {
      try {
        await onDelete(stock.id);
        setDeleteConfirmOpen(false);
      } catch (error) {
        console.error("Failed to delete stock:", error);
      }
    }
  };
  const handleDeleteCancel = () => setDeleteConfirmOpen(false);

  const costBasis = useMemo(() => {
    const q = parseFloat(isEditing ? editedStock.quantity : stock.quantity);
    const c = parseFloat(
      isEditing ? editedStock.costPerStock : stock.costPerStock
    );
    return !isNaN(q) && !isNaN(c) ? q * c : 0;
  }, [stock, editedStock, isEditing]);

  const currentMarketValue = useMemo(() => {
    const q = parseFloat(isEditing ? editedStock.quantity : stock.quantity);
    const p = parseFloat(
      isEditing ? editedStock.currentPrice : stock.currentPrice
    );
    return !isNaN(q) && !isNaN(p) ? q * p : 0;
  }, [stock, editedStock, isEditing]);

  const percentageOfPortfolio = useMemo(
    () =>
      totalCurrentMarketValue > 0 && currentMarketValue > 0
        ? (currentMarketValue / totalCurrentMarketValue) * 100
        : 0,
    [currentMarketValue, totalCurrentMarketValue]
  );

  const gainLoss = useMemo(() => {
    const q =
      parseFloat(isEditing ? editedStock.quantity : stock.quantity) || 0;
    const cp =
      parseFloat(isEditing ? editedStock.currentPrice : stock.currentPrice) ||
      0;
    const cps =
      parseFloat(isEditing ? editedStock.costPerStock : stock.costPerStock) ||
      0;

    if (isNaN(q) || isNaN(cp) || isNaN(cps) || q <= 0 || cp <= 0 || cps <= 0) {
      return 0;
    }

    return q * (cp - cps);
  }, [stock, editedStock, isEditing]);

  const gainLossPercent = useMemo(() => {
    const q =
      parseFloat(isEditing ? editedStock.quantity : stock.quantity) || 0;
    const cp =
      parseFloat(isEditing ? editedStock.currentPrice : stock.currentPrice) ||
      0;
    const cps =
      parseFloat(isEditing ? editedStock.costPerStock : stock.costPerStock) ||
      0;

    // If any required value is missing or invalid, return 0
    if (isNaN(q) || isNaN(cp) || isNaN(cps) || q <= 0 || cp <= 0 || cps <= 0) {
      return 0;
    }

    const costBasis = q * cps;
    if (costBasis === 0) return 0;

    return ((cp - cps) / cps) * 100;
  }, [stock, editedStock, isEditing]);

  const formatQuantity = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "0";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const renderField = (
    label,
    value,
    name,
    type = "text",
    inputProps = {},
    adornment = null,
    align = "left"
  ) => (
    <Grid
      item
      xs={12}
      sm={6}
      sx={{ display: "flex", alignItems: "center", mb: isEditing ? 1 : 0.5 }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: "bold", width: "120px", mr: 1, flexShrink: 0 }}
      >
        {label}:
      </Typography>
      {isEditing ? (
        <TextField
          type={type}
          name={name}
          value={editedStock[name] || ""}
          onChange={handleLocalInputChange}
          onBlur={name === "symbol" ? handleSymbolBlur : undefined}
          placeholder={label}
          fullWidth
          size="small"
          variant="outlined"
          inputProps={inputProps}
          InputProps={
            adornment
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      {adornment}
                    </InputAdornment>
                  ),
                }
              : {}
          }
          sx={{ textAlign: align }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{ wordBreak: "break-word", textAlign: align }}
        >
          {value}
        </Typography>
      )}
    </Grid>
  );

  const renderCurrentPriceField = () => (
    <Grid
      item
      xs={12}
      sm={6}
      sx={{ display: "flex", alignItems: "center", mb: isEditing ? 1 : 0.5 }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: "bold", width: "120px", mr: 1, flexShrink: 0 }}
      >
        Current Price:
      </Typography>
      {isEditing ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
            width: "100%",
            textAlign: "right",
          }}
        >
          <Typography
            variant="body2"
            sx={{ textAlign: "right", width: "100%" }}
          >
            {editedStock.currentPrice
              ? Utils.format_currency_usd(editedStock.currentPrice)
              : "-"}
          </Typography>
          {priceFetchError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, textAlign: "right" }}
            >
              {priceFetchError}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ textAlign: "right", width: "100%" }}>
          {stock.currentPrice
            ? Utils.format_currency_usd(stock.currentPrice)
            : "-"}
        </Typography>
      )}
    </Grid>
  );

  if (isMobile) {
    return (
      <Card
        sx={{
          mb: 2,
          backgroundColor: isEditing ? "action.hover" : "background.paper",
          borderRadius: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        <CardContent>
          <Grid container spacing={2}>
            {/* Action Buttons at the top */}
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
            >
              {isEditing ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                </Box>
              ) : (
                stock.id && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={handleEditClick}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={handleDeleteClick}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )
              )}
            </Grid>

            {/* Symbol and Current Price Row */}
            <Grid item xs={12} sm={6}>
              {renderField("Symbol", stock.symbol, "symbol")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderCurrentPriceField()}
            </Grid>

            {/* Quantity and Cost/Share Row */}
            <Grid item xs={12} sm={6}>
              {renderField("Quantity", stock.quantity, "quantity", "number", {
                min: 0,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderField(
                "Cost/Share",
                stock.costPerStock ? formatDollarValue(stock.costPerStock) : "",
                "costPerStock",
                "number",
                { min: 0, step: "0.01" },
                "$"
              )}
            </Grid>

            {/* Date Purchased and Total Cost Row */}
            <Grid item xs={12} sm={6}>
              {renderField(
                "Date Purchased",
                stock.datePurchased,
                "datePurchased",
                "date",
                {},
                null,
                "center"
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderField(
                "Total Cost",
                formatDollarValue(costBasis),
                "costBasis",
                "text",
                {},
                null,
                "center"
              )}
            </Grid>

            {/* Market Value and Gain/Loss Row */}
            <Grid item xs={12} sm={6}>
              {renderField(
                "Mkt Value",
                formatDollarValue(currentMarketValue),
                "currentMarketValue",
                "text",
                {},
                null,
                "center"
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderField(
                "Gain/Loss",
                <Typography
                  sx={{
                    color: gainLoss >= 0 ? "success.main" : "error.main",
                    fontWeight: "medium",
                  }}
                >
                  {formatDollarValue(gainLoss)}
                </Typography>,
                "gainLoss",
                "text",
                {},
                null,
                "center"
              )}
            </Grid>

            {/* Gain/Loss Percent and Percentage of Portfolio Row */}
            <Grid item xs={12} sm={6}>
              {renderField(
                "Gain/Loss %",
                <Typography
                  sx={{
                    color: gainLossPercent >= 0 ? "success.main" : "error.main",
                    fontWeight: "medium",
                  }}
                >
                  {Utils.format_percent(gainLossPercent / 100)}
                </Typography>,
                "gainLossPercent",
                "text",
                {},
                null,
                "center"
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderField(
                "% of Portfolio",
                `${percentageOfPortfolio.toFixed(2)}%`,
                "percentageOfPortfolio",
                "text",
                {},
                null,
                "center"
              )}
            </Grid>
          </Grid>
        </CardContent>

        <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{stock.symbol}"? This action
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }

  // Desktop Table Row rendering
  return (
    <>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
          backgroundColor: isEditing ? "action.hover" : "transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {/* Actions Column */}
        <TableCell component="th" scope="row" sx={{ width: "120px" }}>
          {isEditing ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleSave}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={handleCancel}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                color="primary"
                size="small"
                onClick={handleEditClick}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                onClick={handleDeleteClick}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </TableCell>

        {/* Symbol Column */}
        <TableCell>
          {isEditing ? (
            <TextField
              type="text"
              name="symbol"
              value={editedStock.symbol || ""}
              onChange={handleLocalInputChange}
              onBlur={handleSymbolBlur}
              placeholder="Symbol"
              fullWidth
              size="small"
              variant="outlined"
            />
          ) : (
            stock.symbol
          )}
        </TableCell>

        {/* Rest of the columns */}
        <TableCell align="right">
          {isEditing ? (
            <TextField
              type="number"
              name="quantity"
              value={editedStock.quantity || ""}
              onChange={handleLocalInputChange}
              placeholder="Quantity"
              fullWidth
              size="small"
              variant="outlined"
              inputProps={{ min: 0 }}
            />
          ) : (
            formatQuantity(stock.quantity)
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              type="number"
              name="costPerStock"
              value={editedStock.costPerStock || ""}
              onChange={handleLocalInputChange}
              placeholder="Cost/Share"
              fullWidth
              size="small"
              variant="outlined"
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          ) : stock.costPerStock ? (
            Utils.format_currency_usd(stock.costPerStock)
          ) : (
            ""
          )}
        </TableCell>
        <TableCell align="center">
          {isEditing ? (
            <TextField
              label="Purchase Date"
              name="datePurchased"
              type="date"
              value={
                editedStock.datePurchased
                  ? new Date(editedStock.datePurchased)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleLocalInputChange}
              fullWidth
              required
              error={!!validationErrors.datePurchased}
              helperText={validationErrors.datePurchased}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
                min: new Date(
                  new Date().setFullYear(new Date().getFullYear() - 50)
                )
                  .toISOString()
                  .split("T")[0],
              }}
            />
          ) : (
            stock.datePurchased
          )}
        </TableCell>
        <TableCell align="right">
          {Utils.format_currency_usd(costBasis)}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                textAlign: "right",
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{ textAlign: "right", width: "100%" }}
              >
                {editedStock.currentPrice
                  ? Utils.format_currency_usd(editedStock.currentPrice)
                  : "-"}
              </Typography>
              {priceFetchError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, textAlign: "right" }}
                >
                  {priceFetchError}
                </Typography>
              )}
            </Box>
          ) : stock.currentPrice ? (
            Utils.format_currency_usd(stock.currentPrice)
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell align="right">
          {Utils.format_currency_usd(currentMarketValue)}
        </TableCell>
        <TableCell align="right">
          <Typography
            sx={{
              color: gainLoss >= 0 ? "success.main" : "error.main",
              fontWeight: "medium",
            }}
          >
            {Utils.format_currency_usd(gainLoss)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            sx={{
              color: gainLossPercent >= 0 ? "success.main" : "error.main",
              fontWeight: "medium",
            }}
          >
            {Utils.format_percent(gainLossPercent / 100)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          {Utils.format_percent(percentageOfPortfolio / 100)}
        </TableCell>
      </TableRow>
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{stock.symbol}"? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

StockRow.propTypes = {
  stock: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  totalCurrentMarketValue: PropTypes.number.isRequired,
  isMobile: PropTypes.bool,
  showTodaysGainColumns: PropTypes.bool,
};

// --- StockDialog Component ---
const StockDialog = ({ open, onClose, stock, onSave, isEditing }) => {
  const [editedStock, setEditedStock] = useState({
    id: null,
    symbol: "",
    quantity: "",
    costPerStock: "",
    datePurchased: new Date().toISOString().split("T")[0],
    currentPrice: "",
  });
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [priceFetchError, setPriceFetchError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    // When stock prop changes, update the edited stock
    if (stock) {
      setEditedStock({
        ...stock,
        id: stock.id || null,
        datePurchased: stock.datePurchased
          ? parseISO(stock.datePurchased)
          : new Date(),
      });
      // If we have a symbol, fetch the current price
      if (stock.symbol) {
        handleFetchPrice(stock.symbol);
      }
    } else {
      // Reset to initial state for new stocks
      setEditedStock({
        id: null,
        symbol: "",
        quantity: "",
        costPerStock: "",
        datePurchased: new Date(),
        currentPrice: "",
      });
    }
    setValidationErrors({});
  }, [stock]);

  const handleSymbolSelect = (symbol) => {
    setEditedStock((prev) => ({ ...prev, symbol }));
    handleFetchPrice(symbol);
  };

  const handleFetchPrice = async (symbolToFetch) => {
    if (!symbolToFetch || !API_KEYS.FMP_API_KEY) {
      setPriceFetchError("API key not configured or symbol is empty.");
      setEditedStock((prev) => ({ ...prev, currentPrice: "" }));
      return;
    }
    setIsFetchingPrice(true);
    setPriceFetchError(null);
    try {
      const response = await fetch(
        `${FMP_API_ENDPOINTS.QUOTE(symbolToFetch.toUpperCase())}?${
          API_KEYS.FMP_API_KEY
        }`
      );
      if (!response.ok) {
        throw new Error(`FMP API request failed: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.length > 0 && data[0].price != null) {
        setEditedStock((prev) => ({
          ...prev,
          currentPrice: data[0].price,
        }));
      } else {
        throw new Error("Price not found for symbol");
      }
    } catch (err) {
      console.error("Failed to fetch price:", err);
      setPriceFetchError(err.message);
      setEditedStock((prev) => ({ ...prev, currentPrice: "" }));
    } finally {
      setIsFetchingPrice(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newEditedStock = { ...editedStock, [name]: value };

    if (name === "symbol") {
      newEditedStock = { ...newEditedStock, [name]: value.toUpperCase() };
      if (value.trim() === "") {
        newEditedStock = { ...newEditedStock, currentPrice: "", symbol: "" };
        setPriceFetchError(null);
      }
    }

    if ((name === "quantity" || name === "costPerStock") && value === "") {
      newEditedStock = { ...newEditedStock, [name]: "" };
    }

    // Preserve the ID when updating other fields
    newEditedStock.id = editedStock.id;

    setEditedStock(newEditedStock);
    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDateChange = (newDate) => {
    setEditedStock((prev) => ({
      ...prev,
      datePurchased: newDate,
    }));
    if (validationErrors.datePurchased) {
      setValidationErrors((prev) => ({ ...prev, datePurchased: undefined }));
    }
  };

  const handleSave = () => {
    if (validateStock()) {
      // Ensure we're passing the correct ID state and all required fields
      const stockToSave = {
        ...editedStock,
        id: editedStock.id || null, // Ensure id is null for new stocks
        quantity: parseFloat(editedStock.quantity),
        costPerStock: parseFloat(editedStock.costPerStock),
        currentPrice: parseFloat(editedStock.currentPrice) || 0,
      };
      onSave(stockToSave);
    }
  };

  const validateStock = () => {
    const errors = {};
    if (!editedStock.symbol?.trim()) {
      errors.symbol = "Symbol is required";
    }
    if (
      !editedStock.quantity ||
      isNaN(editedStock.quantity) ||
      parseFloat(editedStock.quantity) <= 0
    ) {
      errors.quantity = "Valid quantity is required";
    }
    if (
      !editedStock.costPerStock ||
      isNaN(editedStock.costPerStock) ||
      parseFloat(editedStock.costPerStock) <= 0
    ) {
      errors.costPerStock = "Valid cost per share is required";
    }
    if (!editedStock.datePurchased) {
      errors.datePurchased = "Purchase date is required";
    }
    if (
      !editedStock.currentPrice ||
      isNaN(editedStock.currentPrice) ||
      parseFloat(editedStock.currentPrice) <= 0
    ) {
      errors.currentPrice = "Current price is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? "Edit Stock" : "Add New Stock"}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <SymbolSearch
            onSymbolSelect={handleSymbolSelect}
            initialValue={editedStock.symbol}
            fluid
          />
          <TextField
            label="Quantity"
            name="quantity"
            type="number"
            value={editedStock.quantity || ""}
            onChange={handleInputChange}
            fullWidth
            required
            error={!!validationErrors.quantity}
            helperText={validationErrors.quantity}
            inputProps={{ min: 0, step: "1" }}
          />
          <TextField
            label="Cost per Share"
            name="costPerStock"
            type="number"
            value={editedStock.costPerStock || ""}
            onChange={handleInputChange}
            fullWidth
            required
            error={!!validationErrors.costPerStock}
            helperText={validationErrors.costPerStock}
            inputProps={{ min: 0, step: "0.01" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
          <TextField
            label="Purchase Date"
            name="datePurchased"
            type="date"
            value={
              editedStock.datePurchased
                ? new Date(editedStock.datePurchased)
                    .toISOString()
                    .split("T")[0]
                : ""
            }
            onChange={handleInputChange}
            fullWidth
            required
            error={!!validationErrors.datePurchased}
            helperText={validationErrors.datePurchased}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
              min: new Date(
                new Date().setFullYear(new Date().getFullYear() - 50)
              )
                .toISOString()
                .split("T")[0],
            }}
          />
          <TextField
            label="Current Price"
            value={editedStock.currentPrice || ""}
            fullWidth
            disabled
            error={!!validationErrors.currentPrice}
            helperText={validationErrors.currentPrice}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Portfolio = ({
  userPortfolio,
  portfolioStocks,
  isLoading, // This is the global isLoading from parent
  error, // This is the global error from parent
  onSaveStock,
  onDeleteStock,
  onRefreshPortfolio,
  isMobile,
  showTodaysGainColumns = false,
}) => {
  const [editingStockId, setEditingStockId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "symbol",
    direction: "asc",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentStock, setCurrentStock] = useState({
    id: null,
    symbol: "",
    quantity: "",
    costPerStock: "",
    datePurchased: new Date().toISOString().split("T")[0],
    currentPrice: "",
  });

  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const {
    processedPortfolioStocks,
    calculations,
    dayOverDayChange,
    lastUpdated,
  } = usePortfolioCalculations(portfolioStocks, searchTerm, sortConfig);

  const {
    totalCurrentMarketValue: overallTotalCurrentMarketValue,
    totalCostBasis: overallTotalCostBasis,
    totalGainLoss,
    percentageGainLoss,
  } = calculations;

  const tableHeaders = useMemo(
    () => [
      {
        key: "actions",
        label: "Actions",
        disableSort: true,
        width: "90px",
        align: "center",
      },
      { key: "symbol", label: "Symbol", width: "80px" },
      {
        key: "quantity",
        label: "Quantity",
        numeric: true,
        align: "right",
        width: "80px",
      },
      {
        key: "percentageOfPortfolio",
        label: "% of Portfolio",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "costPerStock",
        label: "Cost/Share",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "datePurchased",
        label: "Date Purchased",
        width: "110px",
        align: "center",
      },
      {
        key: "costBasis",
        label: "Total Cost",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "currentPrice",
        label: "Current Price",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "todaysGain",
        label: "Today's Gain",
        numeric: true,
        align: "right",
        width: "110px",
      },
      {
        key: "todaysGainPct",
        label: "Today's Gain %",
        numeric: true,
        align: "right",
        width: "110px",
      },
      {
        key: "currentMarketValue",
        label: "Mkt Value",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "gainLoss",
        label: "Gain/Loss",
        numeric: true,
        align: "right",
        width: "100px",
      },
      {
        key: "gainLossPercent",
        label: "Gain/Loss %",
        numeric: true,
        align: "right",
        width: "100px",
      },
    ],
    [showTodaysGainColumns]
  );

  const handleEdit = (stockId) => {
    const stockToEdit = portfolioStocks.find((s) => s.id === stockId);
    if (stockToEdit) {
      setCurrentStock(stockToEdit);
      setEditingStockId(stockId);
      setDialogOpen(true);
    }
  };

  const handleAddNewStockClick = () => {
    setCurrentStock({
      id: null,
      symbol: "",
      quantity: "",
      costPerStock: "",
      datePurchased: new Date().toISOString().split("T")[0],
      currentPrice: "",
    });
    setEditingStockId(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingStockId(null);
    setCurrentStock({
      id: null,
      symbol: "",
      quantity: "",
      costPerStock: "",
      datePurchased: new Date().toISOString().split("T")[0],
      currentPrice: "",
    });
  };

  const handleDialogSave = async (stockToSave) => {
    setLocalIsLoading(true);
    setLocalError(null);

    // Ensure we have all required fields
    if (
      !stockToSave.symbol ||
      !stockToSave.quantity ||
      !stockToSave.costPerStock ||
      !stockToSave.datePurchased
    ) {
      setLocalError("Please fill in all required fields.");
      setLocalIsLoading(false);
      return;
    }

    // Format the stock data
    const formattedStock = {
      ...stockToSave,
      quantity: parseFloat(stockToSave.quantity),
      costPerStock: parseFloat(stockToSave.costPerStock),
      currentPrice: parseFloat(stockToSave.currentPrice) || 0,
    };

    // For new stocks, ensure id is null
    if (!stockToSave.id) {
      formattedStock.id = null;
    }

    // Determine if this is a new stock or an update
    const isNew = !stockToSave.id;
    const stockIdToUpdate = stockToSave.id;

    try {
      const success = await onSaveStock(formattedStock, isNew, stockIdToUpdate);
      if (success) {
        handleDialogClose();
      } else {
        setLocalError("Failed to save stock. Please try again.");
      }
    } catch (error) {
      setLocalError(error.message || "Failed to save stock. Please try again.");
    } finally {
      setLocalIsLoading(false);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const requestSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const handleDeleteStock = async (stockId) => {
    if (!stockId) return;

    setLocalIsLoading(true);
    setLocalError(null);
    try {
      const success = await onDeleteStock(stockId);
      if (!success) {
        setLocalError("Failed to delete stock. Please try again.");
      }
    } catch (error) {
      setLocalError(
        error.message || "Failed to delete stock. Please try again."
      );
    } finally {
      setLocalIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{
          color: "primary.dark",
          fontWeight: 600,
          mb: 2,
          mt: 0.5,
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          letterSpacing: "-0.02em",
        }}
      >
        {userPortfolio?.portfolioName || "My Stock Portfolio"}
      </Typography>

      {/* Loading and Error States */}
      {isLoading && !localIsLoading && (
        <CircularProgress sx={{ display: "block", margin: "10px auto" }} />
      )}
      {localIsLoading && (
        <CircularProgress sx={{ display: "block", margin: "10px auto" }} />
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ textAlign: "center", mb: 1, borderRadius: 1 }}
        >
          {error}
        </Alert>
      )}
      {localError && (
        <Alert
          severity="error"
          sx={{ textAlign: "center", mb: 1, borderRadius: 1 }}
        >
          {localError}
        </Alert>
      )}

      {(portfolioStocks.length > 0 || dialogOpen) && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1,
            background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container spacing={2} alignItems="stretch">
            {/* Total Cost Basis */}
            <Grid item xs={12} sm={6} md={3}>
              <MuiTooltip title="Total amount invested.">
                <Box
                  textAlign="center"
                  sx={{
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    background:
                      "linear-gradient(145deg, #f5f5f5 0%, #e0e0e0 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {Utils.format_currency_usd(overallTotalCostBasis)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1, mt: 0.5 }}
                  >
                    Total Cost Basis {searchTerm && "(Filtered)"}
                  </Typography>
                </Box>
              </MuiTooltip>
            </Grid>

            {/* Total Market Value */}
            <Grid item xs={12} sm={6} md={3}>
              <MuiTooltip title="Total value at current market prices.">
                <Box
                  textAlign="center"
                  sx={{
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    background:
                      "linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary.main"
                    sx={{ fontWeight: "bold" }}
                  >
                    {Utils.format_currency_usd(overallTotalCurrentMarketValue)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1, mt: 0.5 }}
                  >
                    Total Market Value {searchTerm && "(Filtered)"}
                  </Typography>
                </Box>
              </MuiTooltip>
            </Grid>

            {/* Day over Day Change */}
            <Grid item xs={12} sm={6} md={3}>
              <MuiTooltip title="Change in portfolio value from previous day's closing price">
                <Box
                  textAlign="center"
                  sx={{
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    background:
                      dayOverDayChange?.value > 0
                        ? "linear-gradient(145deg, #e8f5e9 0%, #c8e6c9 100%)"
                        : dayOverDayChange?.value < 0
                        ? "linear-gradient(145deg, #ffebee 0%, #ffcdd2 100%)"
                        : "linear-gradient(145deg, #f5f5f5 0%, #e0e0e0 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color:
                        dayOverDayChange?.value > 0
                          ? "success.main"
                          : dayOverDayChange?.value < 0
                          ? "error.main"
                          : "text.primary",
                    }}
                  >
                    {dayOverDayChange?.isLoading ? (
                      <CircularProgress size={16} />
                    ) : dayOverDayChange?.value === null ? (
                      "-"
                    ) : (
                      `${Utils.format_currency_usd(
                        dayOverDayChange.value
                      )} (${Utils.format_percent(
                        dayOverDayChange.percentage / 100
                      )})`
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1, mt: 0.5 }}
                  >
                    Day over Day Change
                  </Typography>
                  {lastUpdated && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1, mt: 0.5 }}
                    >
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </Typography>
                  )}
                </Box>
              </MuiTooltip>
            </Grid>

            {/* Total Gain/Loss */}
            <Grid item xs={12} sm={6} md={3}>
              <MuiTooltip title="Total Gain or Loss.">
                <Box
                  textAlign="center"
                  sx={{
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    background:
                      totalGainLoss >= 0
                        ? "linear-gradient(145deg, #e8f5e9 0%, #c8e6c9 100%)"
                        : "linear-gradient(145deg, #ffebee 0%, #ffcdd2 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: totalGainLoss >= 0 ? "success.main" : "error.main",
                    }}
                  >
                    {`${Utils.format_currency_usd(
                      totalGainLoss
                    )} (${Utils.format_percent(percentageGainLoss / 100)})`}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1, mt: 0.5 }}
                  >
                    Total Gain/Loss {searchTerm && "(Filtered)"}
                  </Typography>
                </Box>
              </MuiTooltip>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Box>
            <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={12} sm={7} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search Symbol or Date..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={5}
                md={4}
                sx={{ textAlign: { xs: "left", sm: "right" } }}
              >
                {!dialogOpen && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewStockClick}
                    size="small"
                    sx={{
                      borderRadius: 1,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    Add Stock
                  </Button>
                )}
              </Grid>
            </Grid>

            {isMobile ? (
              <Box>
                {dialogOpen && (
                  <StockRow
                    key={currentStock.id}
                    stock={currentStock}
                    onSave={handleDialogSave}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    isEditing={editingStockId === currentStock.id}
                    onCancelEdit={() => {}}
                    onInputChange={() => {}}
                    totalCurrentMarketValue={overallTotalCurrentMarketValue}
                    isMobile={isMobile}
                    showTodaysGainColumns={showTodaysGainColumns}
                  />
                )}
                {processedPortfolioStocks.map((s) => (
                  <StockRow
                    key={s.id}
                    stock={s}
                    onSave={handleDialogSave}
                    onEdit={() => handleEdit(s.id)}
                    onDelete={handleDeleteStock}
                    isEditing={editingStockId === s.id}
                    onCancelEdit={() => {}}
                    onInputChange={() => {}}
                    totalCurrentMarketValue={overallTotalCurrentMarketValue}
                    isMobile={isMobile}
                    showTodaysGainColumns={showTodaysGainColumns}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                  maxHeight: "60vh",
                  overflow: "auto",
                  borderRadius: 1,
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  mb: 4,
                  "& .MuiTableHead-root": {
                    background:
                      "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    "& .MuiTableCell-head": {
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.85rem",
                      borderBottom: "none",
                      padding: "8px 4px",
                      whiteSpace: "nowrap",
                    },
                  },
                  "& .MuiTableBody-root": {
                    "& .MuiTableRow-root": {
                      transition: "background-color 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(33, 150, 243, 0.04)",
                      },
                      "& .MuiTableCell-root": {
                        padding: "4px",
                        whiteSpace: "nowrap",
                      },
                    },
                  },
                }}
              >
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="stock portfolio table"
                  size="small"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      {tableHeaders.map((hC) => (
                        <TableCell
                          key={hC.key}
                          align={
                            hC.headerAlign ||
                            hC.align ||
                            (hC.numeric ? "right" : "left")
                          }
                          sortDirection={
                            sortConfig.key === hC.key
                              ? sortConfig.direction
                              : false
                          }
                          sx={{
                            backgroundColor: "#1976d2",
                            color: "white",
                            fontWeight: "bold",
                            width: hC.width,
                            whiteSpace: "nowrap",
                            "& .MuiTableSortLabel-root": {
                              color: "white",
                              "&:hover": {
                                color: "rgba(255, 255, 255, 0.8)",
                              },
                              "&.MuiTableSortLabel-active": {
                                color: "white",
                              },
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "white !important",
                            },
                          }}
                        >
                          {hC.disableSort ? (
                            hC.label
                          ) : (
                            <TableSortLabel
                              active={sortConfig.key === hC.key}
                              direction={
                                sortConfig.key === hC.key
                                  ? sortConfig.direction
                                  : "asc"
                              }
                              onClick={() => requestSort(hC.key)}
                            >
                              {hC.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dialogOpen && (
                      <StockRow
                        key={currentStock.id}
                        stock={currentStock}
                        onSave={handleDialogSave}
                        onEdit={() => {}}
                        onDelete={() => {}}
                        isEditing={editingStockId === currentStock.id}
                        onCancelEdit={() => {}}
                        onInputChange={() => {}}
                        totalCurrentMarketValue={overallTotalCurrentMarketValue}
                        isMobile={isMobile}
                        showTodaysGainColumns={showTodaysGainColumns}
                      />
                    )}
                    {processedPortfolioStocks.map((s) => (
                      <TableRow key={s.id} hover>
                        {/* Static group cells */}
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(s.id)}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteStock(s.id)}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#fffde7" }}>
                          {s.symbol}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          {s.quantity}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          {s.percentageOfPortfolio
                            ? Utils.format_percent(
                                s.percentageOfPortfolio / 100
                              )
                            : "--"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          {Utils.format_currency_usd(s.costPerStock)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          {s.datePurchased
                            ? format(new Date(s.datePurchased), "yyyy-MM-dd")
                            : ""}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#fffde7" }}
                        >
                          {Utils.format_currency_usd(
                            (parseFloat(s.quantity) || 0) *
                              (parseFloat(s.costPerStock) || 0)
                          )}
                        </TableCell>
                        {/* Dynamic group cells */}
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#e3f2fd" }}
                        >
                          {s.currentPrice
                            ? Utils.format_currency_usd(s.currentPrice)
                            : "--"}
                        </TableCell>
                        {showTodaysGainColumns && (
                          <>
                            <TableCell
                              align="right"
                              sx={{ backgroundColor: "#e3f2fd" }}
                            >
                              {s.todaysGain !== undefined
                                ? Utils.format_currency_usd(s.todaysGain)
                                : "--"}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ backgroundColor: "#e3f2fd" }}
                            >
                              {s.todaysGainPct !== undefined
                                ? Utils.format_percent(s.todaysGainPct / 100)
                                : "--"}
                            </TableCell>
                          </>
                        )}
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#e3f2fd" }}
                        >
                          {Utils.format_currency_usd(
                            (parseFloat(s.quantity) || 0) *
                              (parseFloat(s.currentPrice) || 0)
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#e3f2fd" }}
                        >
                          {Utils.format_currency_usd(
                            (parseFloat(s.quantity) || 0) *
                              ((parseFloat(s.currentPrice) || 0) -
                                (parseFloat(s.costPerStock) || 0))
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: "#e3f2fd" }}
                        >
                          {s.costPerStock && s.currentPrice
                            ? Utils.format_percent(
                                (s.currentPrice - s.costPerStock) /
                                  s.costPerStock
                              )
                            : "--"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {processedPortfolioStocks.length === 0 &&
              !dialogOpen &&
              searchTerm && (
                <Paper
                  elevation={2}
                  sx={{
                    textAlign: "center",
                    p: 2,
                    mt: 1,
                    borderRadius: 1,
                    background:
                      "linear-gradient(145deg, #f5f5f5 0%, #e0e0e0 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <SearchIcon
                    sx={{
                      fontSize: 36,
                      color: "text.secondary",
                      mb: 1,
                      opacity: 0.7,
                    }}
                  />
                  <Typography variant="subtitle1">
                    No stocks match search.
                  </Typography>
                </Paper>
              )}
            {portfolioStocks.length === 0 &&
              !dialogOpen &&
              !searchTerm &&
              !isLoading && (
                <Paper
                  elevation={2}
                  sx={{
                    textAlign: "center",
                    p: 2,
                    mt: 1,
                    borderRadius: 1,
                    background:
                      "linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 36,
                      color: "primary.main",
                      mb: 1,
                      opacity: 0.7,
                    }}
                  />
                  <Typography variant="subtitle1">
                    Portfolio is empty. Add your first stock!
                  </Typography>
                </Paper>
              )}
          </Box>
        </Grid>
      </Grid>

      <StockDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        stock={currentStock}
        onSave={handleDialogSave}
        isEditing={!!currentStock.id}
      />
    </Container>
  );
};

Portfolio.propTypes = {
  userPortfolio: PropTypes.object,
  portfolioStocks: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onSaveStock: PropTypes.func.isRequired,
  onDeleteStock: PropTypes.func.isRequired,
  onRefreshPortfolio: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  showTodaysGainColumns: PropTypes.bool,
};

export default Portfolio;
